import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, PlayCircle, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

export function WorkflowsPage() {
  const workflows = useQuery({
    queryKey: ['workflows'],
    queryFn: async () => (await api.get('/api/workflows')).data,
  });

  const runs = useQuery({
    queryKey: ['runs'],
    queryFn: async () => (await api.get('/api/workflows/runs/list?pageSize=50')).data,
    refetchInterval: (q) => {
      const items = (q.state.data as { items?: { status: string }[] } | undefined)?.items;
      return items?.some(
        (r) => r.status === 'QUEUED' || r.status === 'RUNNING',
      )
        ? 3000
        : false;
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Workflows</h1>
        <p className="text-sm text-muted-foreground">
          AI extraction pipelines with human review checkpoints.
        </p>
      </div>

      {/* Definitions */}
      <Card>
        <CardHeader>
          <CardTitle>Available workflows</CardTitle>
          <CardDescription>Select a workflow when running against a document.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {workflows.data?.items?.map((w: {
            id: string;
            name: string;
            description: string | null;
            type: string;
            version: number;
          }) => (
            <div key={w.id} className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{w.name}</span>
                  <Badge variant="secondary">v{w.version}</Badge>
                  <Badge variant="outline">{w.type.replace(/_/g, ' ')}</Badge>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">{w.description}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Runs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workflow</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead>Started</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.data?.items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    No runs yet.
                  </TableCell>
                </TableRow>
              )}
              {runs.data?.items?.map((r: {
                id: string;
                workflow: { name: string };
                status: string;
                completedSteps: number;
                totalSteps: number;
                totalCost: number;
                createdAt: string;
              }) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.workflow.name}</TableCell>
                  <TableCell><RunStatus status={r.status} /></TableCell>
                  <TableCell>
                    {r.completedSteps}/{r.totalSteps}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(r.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/workflows/runs/${r.id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      Open
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function RunStatus({ status }: { status: string }) {
  if (status === 'COMPLETED')
    return (
      <Badge variant="success">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Completed
      </Badge>
    );
  if (status === 'FAILED' || status === 'CANCELLED')
    return (
      <Badge variant="destructive">
        <XCircle className="mr-1 h-3 w-3" /> {status}
      </Badge>
    );
  if (status === 'AWAITING_REVIEW')
    return (
      <Badge variant="warning">
        <Clock className="mr-1 h-3 w-3" /> Awaiting review
      </Badge>
    );
  if (status === 'RUNNING')
    return (
      <Badge variant="info">
        <PlayCircle className="mr-1 h-3 w-3" /> Running
      </Badge>
    );
  return <Badge variant="secondary">{status}</Badge>;
}
